import { CROPS, type CropKey } from '@bb/shared';

/**
 * Crop art, drawn in code like everything else. `growth` 0–1: seed → sprout →
 * leafy plant; at 1 the crop itself shows, ready to pick.
 */
export function drawCrop(c: CanvasRenderingContext2D, crop: CropKey, x: number, y: number, size: number, growth = 1) {
  const info = CROPS[crop];
  const s = size / 40;
  c.save();
  c.translate(x, y);
  c.scale(s, s);
  if (growth < 1) {
    // Growing: a mound, then a sprout, then leaves.
    c.fillStyle = '#7a5235';
    c.beginPath();
    c.ellipse(0, 10, 12, 5, 0, 0, Math.PI * 2);
    c.fill();
    const h = 4 + growth * 16;
    c.strokeStyle = info.leaf;
    c.lineWidth = 3;
    c.lineCap = 'round';
    c.beginPath();
    c.moveTo(0, 8);
    c.lineTo(0, 8 - h);
    c.stroke();
    c.fillStyle = info.leaf;
    const leaves = growth < 0.34 ? 1 : growth < 0.67 ? 2 : 3;
    for (let i = 0; i < leaves; i++) {
      const ly = 8 - h + 3 + i * 4;
      c.beginPath();
      c.ellipse(-5, ly, 5, 2.5, -0.5, 0, Math.PI * 2);
      c.ellipse(5, ly, 5, 2.5, 0.5, 0, Math.PI * 2);
      c.fill();
    }
    c.restore();
    return;
  }
  const leaf = () => {
    c.fillStyle = info.leaf;
    c.beginPath();
    c.ellipse(-4, -12, 3, 7, -0.4, 0, Math.PI * 2);
    c.ellipse(4, -12, 3, 7, 0.4, 0, Math.PI * 2);
    c.fill();
  };
  c.fillStyle = info.color;
  switch (crop) {
    case 'carrot':
      leaf();
      c.fillStyle = info.color;
      c.beginPath();
      c.moveTo(-7, -6);
      c.lineTo(7, -6);
      c.lineTo(0, 16);
      c.closePath();
      c.fill();
      break;
    case 'potato':
      c.beginPath();
      c.ellipse(0, 2, 13, 10, 0.3, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = 'rgba(0,0,0,.18)';
      for (const [dx, dy] of [[-5, 0], [4, -3], [2, 6]]) c.fillRect(dx!, dy!, 2, 2);
      break;
    case 'lettuce':
      for (const [dx, dy, r] of [[-6, 2, 9], [6, 2, 9], [0, -3, 10], [0, 6, 8]] as const) {
        c.fillStyle = r === 10 ? '#b6e38f' : info.color;
        c.beginPath();
        c.arc(dx, dy, r, 0, Math.PI * 2);
        c.fill();
      }
      break;
    case 'tomato':
    case 'pumpkin':
      c.beginPath();
      c.ellipse(0, 3, crop === 'pumpkin' ? 15 : 11, crop === 'pumpkin' ? 12 : 11, 0, 0, Math.PI * 2);
      c.fill();
      if (crop === 'pumpkin') {
        c.strokeStyle = 'rgba(0,0,0,.15)';
        c.lineWidth = 1.5;
        for (const dx of [-6, 0, 6]) {
          c.beginPath();
          c.ellipse(dx, 3, 4, 11, 0, 0, Math.PI * 2);
          c.stroke();
        }
      }
      c.fillStyle = info.leaf;
      c.fillRect(-2, -11, 4, 6);
      break;
    case 'corn':
      c.fillStyle = info.leaf;
      c.beginPath();
      c.ellipse(-6, 4, 5, 13, -0.3, 0, Math.PI * 2);
      c.ellipse(6, 4, 5, 13, 0.3, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = info.color;
      c.beginPath();
      c.ellipse(0, 0, 6, 14, 0, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = 'rgba(0,0,0,.12)';
      for (let i = -10; i <= 10; i += 4) c.fillRect(-4, i, 8, 1);
      break;
    case 'strawberry':
      c.beginPath();
      c.moveTo(0, 15);
      c.bezierCurveTo(-16, 2, -10, -10, 0, -6);
      c.bezierCurveTo(10, -10, 16, 2, 0, 15);
      c.fill();
      c.fillStyle = '#ffe7a0';
      for (const [dx, dy] of [[-4, 0], [4, 1], [0, 6], [-2, -3], [3, -4]]) c.fillRect(dx!, dy!, 1.6, 2.2);
      c.fillStyle = info.leaf;
      c.beginPath();
      c.ellipse(0, -8, 7, 3, 0, 0, Math.PI * 2);
      c.fill();
      break;
    case 'watermelon':
      c.beginPath();
      c.ellipse(0, 2, 16, 11, 0, 0, Math.PI * 2);
      c.fill();
      c.strokeStyle = '#9fd17a';
      c.lineWidth = 2;
      for (const dx of [-8, 0, 8]) {
        c.beginPath();
        c.ellipse(dx, 2, 2, 10, 0, 0, Math.PI * 2);
        c.stroke();
      }
      break;
    case 'starfruit':
      c.beginPath();
      for (let i = 0; i < 10; i++) {
        const r = i % 2 ? 6 : 15;
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        c[i ? 'lineTo' : 'moveTo'](Math.cos(a) * r, Math.sin(a) * r + 2);
      }
      c.closePath();
      c.fill();
      c.fillStyle = 'rgba(255,255,255,.6)';
      c.beginPath();
      c.arc(-4, -3, 2.5, 0, Math.PI * 2);
      c.fill();
      break;
  }
  c.restore();
}
